/**
 * SAIT-22, 06.10.2026: отзывы о компании, а не о конкретном товаре.
 * Главная и карточка — над футером после просмотренных товаров (SAIT-146, SAIT-160).
 * Цвета и обводки внутри Shadow DOM настраиваются CSS в кабинете ReviewLab.
 */
import { ref, nextTick, onMounted, onBeforeUnmount } from 'ui.vue3';
import { vplSiteStore } from 'vpl.tas';
import { loadReviewLab } from '../reviewlab-loader';
import { usePageContentReady } from '../composables/use-page-content-ready';
import './company-reviews.css';

// Оплаченный виджет заказчика из окружения (blocks/header.php); пусто = блока нет.
const WIDGET_ID = String(window.TAS_REVIEWLAB_WIDGET_ID || '');
const template = `
    <section v-if="enabled" v-show="pageReady" class="trw-reviews" aria-label="Отзывы о TAS.PRO">
        <div class="container">
            <div class="section__heading trw-reviews__heading">
                <div class="section__title-wp">
                    <h2 class="section__title">Отзывы о TAS.PRO</h2>
                </div>
            </div>
            <div ref="widgetHost" :aria-busy="status === 'loading'"></div>
            <p v-if="status === 'loading'" role="status">Загружаем отзывы…</p>
            <div v-if="status === 'error'">
                <p role="status">Отзывы временно недоступны.</p>
                <button type="button" class="btn btn--border" @click="mountWidget">Повторить</button>
            </div>
        </div>
    </section>
`;

export const CompanyReviews = {
    props: {
        structKey: { type: [String, Number], default: null },
    },
    emits: [],
    setup() {
        const widgetHost = ref(null);
        const { pageReady, waitForPageContent } = usePageContentReady(vplSiteStore(), widgetHost);
        const status = ref('idle');
        let active = true;
        let widget = null;
        let visibilityObserver = null;
        let resolveVisibility = null;

        const waitForWidgetView = () => {
            if (!('IntersectionObserver' in window)) {
                return Promise.resolve(true);
            }
            const target = widgetHost.value?.closest('.trw-reviews');
            if (!target) {
                return Promise.resolve(false);
            }
            return new Promise(resolve => {
                resolveVisibility = resolve;
                // SAIT-22: начинаем загрузку за 200 px до появления блока отзывов.
                visibilityObserver = new IntersectionObserver(entries => {
                    if (!active || !entries.some(entry => entry.isIntersecting)) {
                        return;
                    }
                    visibilityObserver.disconnect();
                    visibilityObserver = null;
                    resolveVisibility = null;
                    resolve(true);
                }, { rootMargin: '200px' });
                visibilityObserver.observe(target);
            });
        };

        const mountWidget = async () => {
            if (!WIDGET_ID || status.value === 'loading' || widget) {
                return;
            }
            status.value = 'loading';
            try {
                const ready = await waitForPageContent();
                if (!ready || !active) {
                    return;
                }
                // v-show должен открыть блок до проверки его положения во viewport.
                await nextTick();
                if (!active) {
                    return;
                }
                const visible = await waitForWidgetView();
                if (!visible || !active) {
                    return;
                }
                await loadReviewLab();
                // SPA могла сменить страницу, пока скачивался внешний скрипт.
                if (!active || !widgetHost.value) {
                    return;
                }
                // Нативный узел не проходит компилятор Vue и остаётся во владении ReviewLab.
                widget = document.createElement('review-lab');
                widget.setAttribute('data-widgetid', WIDGET_ID);
                widget.style.display = 'block';
                widgetHost.value.appendChild(widget);
                status.value = 'ready';
            } catch (error) {
                if (active) {
                    status.value = 'error';
                }
            }
        };

        onMounted(mountWidget);
        onBeforeUnmount(() => {
            active = false;
            visibilityObserver?.disconnect();
            visibilityObserver = null;
            resolveVisibility?.(false);
            resolveVisibility = null;
            if (widget) {
                // Отключение custom element запускает штатную очистку провайдера.
                widget.remove();
                widget = null;
            }
        });

        return { enabled: Boolean(WIDGET_ID), widgetHost, pageReady, status, mountWidget };
    },
    template,
};
